<script setup lang="ts">
import { computed, watch } from "vue";
import { useStore } from "vuex";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import ArtworkImage from "components/jiffy/vue-product-details-dtf/ArtworkImage.vue";
import ArtworkQualityConfigPanel from "components/jiffy/vue-product-details-dtf/ArtworkQualityConfigPanel/ArtworkQualityConfigPanel.vue";

const props = defineProps<{ sideKey: string }>();

const store = useStore();
const artworkCamelCase = computed(() => {
  const defaultArtwork = {
    enhancementTier: "full_enhancements",
    pricing: objNamesToCamelCase(store.getters.config.artwork_prices.full_enhancements)
  };
  const artwork = store.getters[`artwork_${props.sideKey}/artworkCamelCase`];
  return artwork ?? defaultArtwork;
});
const index = computed(() => (props.sideKey === "front" ? 0 : 1));
const isInitial = computed(() => {
  const artwork = store.getters[`artwork_${props.sideKey}/artworkCamelCase`];
  return !artwork || artwork?.isSimulated;
});

// Add a watcher to update the artwork comment in the store
watch(
  () => artworkCamelCase.value.enhancementNote,
  note => {
    if (typeof note === "undefined") return; // Avoid saving undefined note while deleting artwork
    store.commit(`artwork_${props.sideKey}/setArtworkComment`, note);
  }
);
</script>

<template>
  <div class="image-review-ab" :class="{ 'image-review-ab--initial': isInitial }">
    <ArtworkImage
      class="image-review-ab__image"
      :index="index"
      :artwork="artworkCamelCase"
      with-made
      show-full-screen-button
      active-config-section="jiffy-print-perfect"
    />
    <ArtworkQualityConfigPanel
      :index="index"
      :artwork="artworkCamelCase"
      :reorder-enabled="false"
      :enable-ab-monetization-ui-v2="store.getters.experiments.ab_monetization_ui_v2 === 1"
    />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/pod/settings";
@import "frontend/init/jiffy/mixin";
@import "frontend/init/jiffy/button/mixin";

.image-review-ab {
  @include with-container(container, $pod-pdp-desktop-response) {
    padding: 8px;
    border-radius: 8px;
    border: 1px solid $pod-gray-modern-200;
    background-color: $white;
    box-shadow: $pod-card-shadow;
  }

  &--initial {
    opacity: 0.3;
    pointer-events: none;
    user-select: none;
  }

  &__image {
    margin-bottom: 16px;
  }
}
</style>
